import React, { useState, useEffect } from 'react'
import { useI18n } from '../../hooks/useI18n'
import { useAuth } from '../../hooks/useAuth'
import { useCare } from '../../hooks/useCare'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '../../config/firebase'
import { genConfig } from 'react-nice-avatar'
import { useThemeContext } from '../../contexts/ThemeContext'
import NotificationSettings from '../notifications/NotificationSettings'
import ActiveReminders from '../notifications/ActiveReminders'
import Avatar from '../common/Avatar'
import AvatarEditor from '../common/AvatarEditor'
import { getSubscriptionLimit } from '../../config/subscription'

const ProfilePage: React.FC = () => {
  const { t, currentLanguage, setLanguage } = useI18n()
  const { user, userProfile, logout, refreshUserProfile, deleteAccount } = useAuth()
  const { currentMembership, currentCase, userCases } = useCare()
  const { theme, toggleTheme } = useThemeContext()
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'reminders'>('profile')

  // Avatar state
  const [showAvatarSelector, setShowAvatarSelector] = useState(false)
  const [avatarSeed, setAvatarSeed] = useState<string>('')
  const [avatarCustomization, setAvatarCustomization] = useState<any>({})
  const [isDeleting, setIsDeleting] = useState(false)

  // Load avatar from userProfile when available
  useEffect(() => {
    if (userProfile) {
      setAvatarSeed(userProfile.avatarSeed || user?.email || user?.uid || '')
      setAvatarCustomization(userProfile.avatarCustomization || {})
    } else if (user) {
      // Fallback to user email/uid if no profile yet
      const seed = user.email || user.uid || ''
      setAvatarSeed(seed)
      setAvatarCustomization(genConfig(seed))
    }
  }, [userProfile, user])

  // Debug function
  const handleAvatarClick = () => {
    console.log('handleAvatarClick called, current state:', showAvatarSelector)
    console.log('user:', user)
    console.log('avatarSeed:', avatarSeed)
    setShowAvatarSelector(true)
    console.log('setShowAvatarSelector(true) called')
  }

  // Debug: log when showAvatarSelector changes
  React.useEffect(() => {
    console.log('showAvatarSelector changed to:', showAvatarSelector)
  }, [showAvatarSelector])

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  const handleUpdateAvatar = async (customization: any, seed: string) => {
    if (!user?.uid) return

    try {
      const userRef = doc(db, 'users', user.uid)
      await updateDoc(userRef, {
        avatarSeed: seed,
        avatarCustomization: customization,
        updatedAt: new Date()
      })

      setAvatarCustomization(customization)
      setAvatarSeed(seed)
      setShowAvatarSelector(false)

      // Refresh user profile to sync avatar in sidebar
      await refreshUserProfile()

      // NUEVA FUNCIONALIDAD: Si el usuario es el asistido (care_recipient), 
      // también actualizar la información del caso
      if (currentMembership?.role === 'care_recipient' && currentCase?.profileUserId === user.uid) {
        try {
          const caseRef = doc(db, 'care-cases', currentCase.id)
          await updateDoc(caseRef, {
            'profileInfo.avatarSeed': seed,
            'profileInfo.avatarCustomization': customization,
            updatedAt: new Date()
          })

          console.log('✅ Avatar sincronizado con información del caso')
        } catch (error) {
          console.error('Error sincronizando avatar con caso:', error)
        }
      }

    } catch (error) {
      console.error('Error updating avatar:', error)
    }
  }

  const generateNewAvatar = () => {
    const newSeed = Math.random().toString(36).substring(2, 15)
    const newConfig = genConfig(newSeed)
    setAvatarSeed(newSeed)
    setAvatarCustomization(newConfig)
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('profile')}
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Gestiona tu perfil y configuraciones de la aplicación
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-600">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'profile'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              👤 {t('profile')}
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'notifications'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              🔔 {t('notifications')}
            </button>
            <button
              onClick={() => setActiveTab('reminders')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'reminders'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              ⏰ {t('reminders')}
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'profile' && (
          <>
            {/* Subscription Status */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Suscripción
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${userProfile?.subscriptionPlan === 'premium'
                  ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-sm'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                  {userProfile?.subscriptionPlan === 'premium' ? 'Premium 👑' : 'Básico'}
                </span>
              </div>

              <div className="space-y-6">
                {/* Cases Usage */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-300">Mis Casos de Cuidado</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {userCases.length} / {getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxCases') === Infinity ? '∞' : getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxCases')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full ${userProfile?.subscriptionPlan === 'premium' ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : 'bg-primary'
                        }`}
                      style={{
                        width: `${Math.min(
                          (userCases.length / (getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxCases') === Infinity ? 10 : getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxCases'))) * 100,
                          100
                        )}%`
                      }}
                    ></div>
                  </div>
                </div>

                {/* Plan Limits Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-700">
                    <p className="text-gray-500 dark:text-gray-400 mb-1">Miembros por Caso</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      Max {getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxMembersPerCase') === Infinity ? 'Ilimitados' : getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxMembersPerCase')}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-700">
                    <p className="text-gray-500 dark:text-gray-400 mb-1">Medicamentos</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      Max {getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxMedications') === Infinity ? 'Ilimitados' : getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxMedications')}
                    </p>
                  </div>
                </div>

                {/* Upgrade Button */}
                {userProfile?.subscriptionPlan !== 'premium' && (
                  <button
                    onClick={() => window.location.href = '/pricing'}
                    className="w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg shadow-sm transition-all transform hover:scale-[1.01] flex items-center justify-center gap-2"
                  >
                    <span>Mejorar Plan</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                  </button>
                )}
              </div>
            </div>

            {/* User Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('personal-info')}
              </h3>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Avatar
                      user={{
                        type: 'human',
                        name: user?.displayName || user?.email || 'Usuario',
                        userId: user?.uid || avatarSeed || undefined,
                        email: user?.email || undefined,
                        avatarCustomization: avatarCustomization
                      }}
                      size="xl"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {user?.displayName || user?.email}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {user?.email}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t('user-since')}: {user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'N/A'}
                    </p>
                    <button
                      onClick={handleAvatarClick}
                      className="text-sm text-primary hover:text-primary/80 transition-colors mt-2"
                    >
                      {t('change-avatar')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Avatar Editor - Componente reutilizable */}
            <AvatarEditor
              isOpen={showAvatarSelector}
              onClose={() => setShowAvatarSelector(false)}
              onSave={handleUpdateAvatar}
              initialSeed={avatarSeed}
              initialCustomization={avatarCustomization}
              title={t('customize-avatar')}
              userName={user?.displayName || user?.email || 'Usuario'}
            />

            {/* App Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('settings')}
              </h3>

              <div className="space-y-4">
                {/* Language */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {t('language')}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Cambia el idioma de la aplicación
                    </p>
                  </div>
                  <select
                    value={currentLanguage}
                    onChange={(e) => setLanguage(e.target.value as 'es' | 'en' | 'pt')}
                    className="input w-32"
                  >
                    <option value="es">🇪🇸 Español</option>
                    <option value="en">🇺🇸 English</option>
                    <option value="pt">🇧🇷 Português</option>
                  </select>
                </div>

                {/* Theme */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {t('theme')}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Cambia entre modo claro y oscuro
                    </p>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {theme === 'dark' ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                        </svg>
                        <span>{t('light')}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                        </svg>
                        <span>{t('dark')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Account Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('account-actions')}
              </h3>

              <div className="space-y-6">
                {/* Logout Section */}
                <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                        {t('logout')}
                      </h4>
                      <p className="text-sm text-red-600 dark:text-red-300 mb-3">
                        {t('logout-description')}
                      </p>
                      <button
                        onClick={handleLogout}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('logout')}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Account Info */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        {t('account')}
                      </h4>
                      <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                        <p><strong>Email:</strong> {user?.email}</p>
                        <p><strong>{t('user-since')}:</strong> {user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'N/A'}</p>
                        <p><strong>{t('last-access')}:</strong> {user?.metadata?.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delete Account Section */}
                <div className="border border-red-200 dark:border-red-900/50 rounded-lg p-4 bg-red-50 dark:bg-red-900/10">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                        Eliminar cuenta
                      </h4>
                      <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                        Elimina permanentemente tu cuenta y todos tus datos. Esta acción no se puede deshacer.
                      </p>
                      <button
                        disabled={isDeleting}
                        onClick={async () => {
                          if (window.confirm('¿Estás seguro de que deseas eliminar tu cuenta? Esta acción es irreversible y eliminará todos tus datos.')) {
                            setIsDeleting(true)
                            try {
                              await deleteAccount()
                            } catch (error: any) {
                              console.error('Error deleting account:', error)
                              setIsDeleting(false)

                              if (error.message === 'security-reauth-needed' || error.code === 'auth/requires-recent-login') {
                                alert('Por seguridad, necesitas volver a iniciar sesión antes de eliminar tu cuenta. Te redirigiremos al inicio de sesión.')
                                await logout()
                              } else {
                                alert('Hubo un error al eliminar la cuenta (' + (error.message || 'desconocido') + '). Por favor, inténtalo de nuevo más tarde.')
                              }
                            }
                          }
                        }}
                        className={`inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {isDeleting ? 'Eliminando...' : 'Eliminar mi cuenta'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'notifications' && <NotificationSettings />}

        {activeTab === 'reminders' && <ActiveReminders />}
      </div>
    </div>
  )
}

export default ProfilePage 